import { useRef, useState, type MouseEvent } from "react";
import { ChevronDown } from "lucide-react";
import { useReducedMotion } from "framer-motion";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { Stagger, StaggerItem } from "@/shared/components/motion/Stagger";
import { cn } from "@/shared/lib/utils";

const FAQS = [
  {
    q: "Do guests need an account to share their details?",
    a: "Not at all. They simply open your link, add their information, and you review it later — no sign-up on their end.",
  },
  {
    q: "Can I protect a collect link?",
    a: "Yes. Add an optional access code, shown right on the link page, so you can share it with confidence.",
  },
  {
    q: "What happens when someone's details look like a duplicate?",
    a: "Nothing merges on its own. When a new entry resembles an existing contact by name or email, Guestbook quietly flags it and lets you merge or keep both.",
  },
  {
    q: "How do households work for invitations?",
    a: "A household holds the shared mailing address, so it exports as a single line — one invitation to one address — while every named member stays gently tracked beneath it.",
  },
  {
    q: "Can I import from my existing spreadsheet?",
    a: "Of course. Upload a CSV or Excel file, match your columns to the right fields, preview the first few rows, and bring them in.",
  },
  {
    q: "What can I export for my print shop or planner?",
    a: "Any list, formatted to fit: choose your columns, rename headers, combine address fields, collapse households, and save the layout to reuse whenever you like.",
  },
];

/**
 * A single FAQ disclosure. Built on native `<details>`/`<summary>` for an
 * accessible, keyboard-friendly toggle (no bespoke primitive, no raw `<button>`,
 * which the Button primitive's uppercase identity in design.md §10 rules out for
 * a sentence-length question). The answer slides open via the CSS
 * `grid-template-rows: 0fr → 1fr` trick; on close we keep the content mounted
 * until the slide finishes, then collapse the native `open`. Reduced motion
 * falls back to an instant native toggle.
 */
function FaqItem({ q, a }: { q: string; a: string }) {
  const reduced = useReducedMotion();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  function toggle(event: MouseEvent<HTMLElement>) {
    event.preventDefault();
    const details = detailsRef.current;
    const wrapper = wrapperRef.current;
    if (!details) return;

    if (reduced || !wrapper) {
      details.open = !details.open;
      setOpen(details.open);
      return;
    }

    if (open) {
      // Collapse: animate to 0fr, then unmount the content once the slide ends.
      setOpen(false);
      const finish = (e: TransitionEvent) => {
        if (e.propertyName !== "grid-template-rows") return;
        details.open = false;
        wrapper.removeEventListener("transitionend", finish);
      };
      wrapper.addEventListener("transitionend", finish);
    } else {
      // Expand: mount the content collapsed, then animate to full height.
      details.open = true;
      requestAnimationFrame(() => setOpen(true));
    }
  }

  return (
    <details ref={detailsRef} className="group rounded-xl glass-surface px-6 py-5">
      <summary
        onClick={toggle}
        className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand [&::-webkit-details-marker]:hidden"
      >
        <h3 className="type-heading-xs text-text">{q}</h3>
        <ChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-text-muted transition-transform duration-300",
            open && "rotate-180",
          )}
          aria-hidden
        />
      </summary>
      <div
        ref={wrapperRef}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <p className="type-body-base pt-3 text-text-muted">{a}</p>
        </div>
      </div>
    </details>
  );
}

/**
 * Frequently asked questions. Each answer expands with a smooth slide-open
 * toggle (see `FaqItem`).
 */
export function FaqSection() {
  return (
    <Section spacing="marketing" id="faq">
      <Container size="5xl">
        <div className="mb-16 flex flex-col items-center gap-4 text-center">
          <p className="type-label-sm tracking-wider uppercase text-text-muted">
            Questions
          </p>
          <h2 className="type-heading-md text-text">Frequently asked questions</h2>
        </div>

        <Stagger className="mx-auto flex max-w-3xl flex-col gap-4">
          {FAQS.map((faq) => (
            <StaggerItem key={faq.q}>
              <FaqItem q={faq.q} a={faq.a} />
            </StaggerItem>
          ))}
        </Stagger>
      </Container>
    </Section>
  );
}
